import { useEffect } from "react";
import { useAppDispatch } from "../store/hooks";
import { getFreshToken } from "../store/auth-actions";

const CHECK_INTERVAL_MS = 60_000;

// The refresh timer in App can't be trusted alone: browsers suspend timers
// while a laptop sleeps and freeze background tabs, so after a weekend the
// token is long dead on a page that still looks logged in. These are the
// moments a tab comes back to life, so the token is re-checked on each of them.
const useSessionKeepAlive = (refreshToken: string | null) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!refreshToken) {
      return;
    }

    const check = () => {
      if (document.visibilityState === "visible") {
        // A failure here retries on the next trigger; one that really ended
        // the session has already sent the user to the login form.
        dispatch(getFreshToken()).catch(() => {});
      }
    };

    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    const interval = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
      clearInterval(interval);
    };
  }, [refreshToken, dispatch]);
};

export default useSessionKeepAlive;
